import { NgView } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { ApiService, CrudClient } from "@app/services/api.service";
import { AuthService } from "@app/services/auth.service";
import { CrudService } from "@app/services/crud.service";
import { DialogService } from "@app/services/dialog.service";
import { DrawerService } from "@app/services/drawer.service";
import { SignalrService } from "@app/services/signalr.service";
import { BaseController, IBaseScope } from "@app/views/base.view";
import { DialogButton, eAlertType, eViewType, IRootScope } from "@models";
import { Constructor, events, icons } from "@tools";
import { StateParams, StateService, Transition, TransitionService } from "@uirouter/angularjs";
import * as angular from "angular";

export interface ICrudScope<T> extends IBaseScope {
    model: T;
    modelType: string;
    save: () => ng.IPromise<any>;
    cancel: () => void;
    delete: () => ng.IPromise<any>;
    isEdit: boolean;
    isNew: boolean;
    isView: boolean;
    form: ng.IFormController;
    validation: any;
    offTransitionCallback: () => void;
}

@NgView({
    token: Token.CrudView,
    dependencies: [
        Token.$Scope,
        Token.CrudService,
        Token.Model
    ],
    resolves: [
        {
            name: "model",
            dependencies: [
                Token.$StateParams,
                Token.AuthService,
                Token.$RootScope,
                Token.ApiService
            ],
            // tslint:disable-next-line:object-literal-shorthand
            fn: function( // need to use "function" instead of arrow here because we use "this" to get the api client
                $stateParams: StateParams,
                authService: AuthService,
                $rootScope: IRootScope,
                api: ApiService
            ) {
                let constructor = this as Constructor<any>;
                // would like to check the state here to get the view type, 
                // but there is a bug that is sending the old state:  
                // https://github.com/angular-ui/ui-router/issues/238
                // instead we'll look for the id state parameter, which shouldn't be there for the new screens
                if ($stateParams.id) {
                    return authService.authPromise.then(() => {
                        return constructor.prototype.client(api).getById($stateParams.id, $rootScope.contentTracker);
                    });
                } else {
                    return authService.authPromise;
                }
            }
        }
    ]
})
export class CrudController<T> extends BaseController {

    constructor(
        public $scope: ICrudScope<T>,
        public agg: CrudService,
        model: T
    ) {

        super();

        agg.$rootScope.setTitle();

        this.$http = agg.$http;
        this.$stateParams = Object.assign({}, agg.$stateParams);//$stateParams is actually a global object that is changed before $destroy is called, so we create a clone of it so that it's static for the life of this view
        this.$rootScope = agg.$rootScope;
        this.$state = agg.$state;
        this.$window = agg.$window;
        this.dialogService = agg.dialogService;
        this.drawerService = agg.drawerService;
        this.$q = agg.$q;
        this.signalrService = agg.signalrService;
        this.api = agg.api;
        this.$transitions = agg.$transitions;

        setTimeout(() => {
            this.$q.all(this.loadPromises).finally(() => {
                this.markChangesClean();
            });
        });

        let offTransition = this.$transitions.onStart({}, (transition: Transition) => {
            if (this.bypassUnsavedConfirmation) return;
            // When selecting items in the adminBaseController tree, for instance, this call
            // is sometimes made, so we'll check to ensure we're actually navigating out of
            // the page.
            if (transition.from().name == transition.to().name) return;
            if (!this.modelIsClean()) {
                let d = this.$q.defer<boolean>();
                this.dialogService.confirm(loc.msg_confirmunsavedchanges, () => {
                    if ($scope.offTransitionCallback) $scope.offTransitionCallback();
                    d.resolve(true);
                }, () => {
                    d.resolve(false);
                });
                return d.promise as Promise<boolean>;
            }
            if ($scope.offTransitionCallback) $scope.offTransitionCallback();
            return true;
        });

        let onNavigateAway = e => {
            if (this.bypassUnsavedConfirmation) return;
            if (!this.modelIsClean()) {
                // If we haven't been passed the event get the window.event
                e = e || window.event;

                let message = loc.msg_confirmunsavedchanges;

                // For IE6-8 and Firefox prior to version 4
                if (e) {
                    e.returnValue = message;
                }

                // For Chrome, Safari, IE8+ and Opera 12+
                return message;
            }
            return null;
        };

        window.onbeforeunload = onNavigateAway;

        let offOnLogin = this.$rootScope.$on(events.loginSuccessful, () => {
            this.subscribe();
        });

        $scope.$on("$destroy", () => {
            window.onbeforeunload = null;
            offTransition();
            offOnLogin();
            this.unsubscribe();
        });

        $scope.model = model;
        $scope.validation = {};

        // set view variables to make it easy for views to bind to
        if (this.$state.current.data) {
            $scope.isEdit = (this.$state.current.data.viewType == eViewType.edit);
            $scope.isNew = (this.$state.current.data.viewType == eViewType.new);
            $scope.isView = (this.$state.current.data.viewType == eViewType.view);
        }

        $scope.cancel = () => {
            this.onCancel();
            this.bypassUnsavedConfirmation = true;
            this.$window.history.back();
            this.unsubscribe();
        };

        $scope.delete = () => {
            return this.delete();
        };

        $scope.save = () => {
            return this.save().then(() => {
                this.markChangesClean();
            });
        };

        this.handleResponse = (responseData, responseStatus) => {
            $scope.validation = this.getValidationObject(responseData, responseStatus);

            if ($scope.validation) {
                let alertMsg = $scope.validation.form || loc.msg_formerror;
                this.dialogService.alert({ type: eAlertType.error, msg: alertMsg });
            } else if (responseStatus == 500) {
                this.dialogService.alert({ type: eAlertType.error, msg: loc.msg_500error });
            }
        };

        this.subscribe();

    }

    public $http: ng.IHttpService;
    public $stateParams: any;
    public $rootScope: IRootScope;
    public $state: StateService;
    public $window: ng.IWindowService;
    public dialogService: DialogService;
    public drawerService: DrawerService;
    public $q: ng.IQService;
    public signalrService: SignalrService;
    public api: ApiService;
    public $transitions: TransitionService;

    public isSaving: boolean;
    protected cleanModel: T;
    protected bypassUnsavedConfirmation: boolean;
    protected loadPromises: Q.IPromise<any>[] = [];
    public deleteMsg: string = loc.msg_confirmdelete;
    public handleResponse: (responseData: any, responseStatus: any) => any;
    
    public client(api: ApiService): CrudClient<T> { throw new Error("This method is abstract"); }
    public onSuccess(): void { throw new Error("This method is abstract"); }
    public onCancel(): void { }
    public onDelete(): void { throw new Error("crudController.onDelete is abstract"); }
    public webSocketEntityType(): string { return null; }
    public waitForRefresh(): boolean { return false; }

    protected save(): ng.IHttpPromise<any> {
        if (this.$scope.isEdit) {
            return this.savePut();
        } else if (this.$scope.isNew) {
            return this.savePost();
        }
    }

    protected savePut(): ng.IPromise<any> {
        if (!this.isSaving) {
            this.isSaving = true;
            return this.$http.put<T>(
                `${this.client(this.api).baseUrl}/${this.$stateParams.id}?waitForRefresh=${this.waitForRefresh()}`,
                this.getModelForSave(),
                { tracker: this.$rootScope.contentTracker }
            ).then(r => {
                this.setUpdatedModel(r.data);
                this.onSuccess();
                this.$scope.$broadcast("saveSuccess", r.data);
            },
                (r: ng.IHttpResponse<T>) => {
                    this.handleResponse(r.data, r.status);
                })
                .finally(() => {
                    this.isSaving = false;
                });
        }
    }

    protected savePost(): ng.IPromise<any> {
        if (!this.isSaving) {
            this.isSaving = true;
            return this.$http.post<T>(
                `${this.client(this.api).baseUrl}?waitForRefresh=${this.waitForRefresh()}`,
                this.getModelForSave(),
                { tracker: this.$rootScope.contentTracker }
            ).then(r => {
                this.setUpdatedModel(r.data);
                this.onSuccess();
                this.$scope.$broadcast("saveSuccess", r.data);
                this.subscribe();
            },
                (r: ng.IHttpResponse<T>) => {
                    this.handleResponse(r.data, r.status);
                })
                .finally(() => {
                    this.isSaving = false;
                });
        }
    }

    public delete() {
        let deferred = this.$q.defer();
        let requiredPromise = this.$q.defer<boolean>();

        if (this.$scope.modelType) {
            requiredPromise.promise = this.api.dependencies.isRequiredByDependency(this.$scope.modelType, [this.$scope.model["id"] as number], this.agg.$rootScope.contentTracker);
        }

        requiredPromise.promise.then(required => {
            this.dialogService.dialog({
                content: required ? loc.msg_deleterequiredbydependency : this.deleteMsg,
                buttons: [
                    new DialogButton(loc.ok, icons.success, args => {
                        this.unsubscribe(); //unsubscribe before we delete, otherwise we get the notification about the deletion before we've left the state
                        this.client(this.api).delete(this.$stateParams.id).then(() => {
                            this.bypassUnsavedConfirmation = true;
                            deferred.resolve();
                            this.onDelete();
                        });
                    }),
                    new DialogButton(loc.cancel, icons.cancel, args => {
                        deferred.reject();
                    })
                ]
            });
        });

        if (!this.$scope.modelType) {
            requiredPromise.resolve(false);
        }

        return deferred.promise;
    }

    protected modelIsClean() {
        return angular.equals(this.cleanModel, angular.copy(this.getModelForSave()));
    }

    protected getModelForSave() {
        return this.$scope.model;
    }

    protected setUpdatedModel(model: T) {
        this.$scope.model = model;
    }

    protected markChangesClean() {
        this.cleanModel = angular.copy(this.getModelForSave());
    }

    public getValidationObject(responseData, responseStatus) {
        let o;
        if (responseStatus == 400) {
            o = {};
            for (let prop in responseData) {
                // ignore the json type property
                if (prop !== "$type") {
                    let newProp = prop === "" ? "form" : prop;
                    let sections = newProp.split(".");
                    sections.forEach((section, key) => {
                        sections[key] = section.toCamelCase();
                    });
                    newProp = sections.join(".");
                    o[newProp] = responseData[prop].join("\n");
                }
            }
        }

        return o;
    }

    private onSocketRelationshipEdited = (editedId: number, relationshipName: string) => {
        let callback = this.relationshipEditedCallbacks[relationshipName];
        if (callback && editedId == this.$stateParams.id) {
            this.client(this.api).getById(editedId).then(r => callback(r));
        }
    }

    /**
     * called when the entity has been deleted.  Can be overriden for custom behavior
     * @param editedId
     */
    protected onSocketDeleted() {
        this.dialogService.alert({
            type: eAlertType.error,
            msg: "Sorry. This entity has just been deleted by another user."
        });
        setTimeout(() => { this.onDelete(); }, 1000);
    }
    private onSocketDeletedDelegate = (editedId: number) => {
        if (editedId == this.$stateParams.id) {
            this.onSocketDeleted();
        }
    }

    /**
     * called when the entity has been edited by another consumer.  Can be overriden for custom behavior.
     * @param editedId
     */
    protected onSocketEdited() {
        this.client(this.api).getById(this.$stateParams.id).then(r => {
            this.setUpdatedModel(r);
        });
    }
    private onSocketEditedDelegate = (editedId: number) => {
        if (editedId == this.$stateParams.id) {
            this.onSocketEdited();
        }
    }

    public subscribe() {
        this.unsubscribe();
        let entityType = this.webSocketEntityType();
        if (!entityType || !this.$stateParams.id) return;

        this.signalrService.invoke("subscribe", this.$rootScope.company.uniqueName, entityType, this.$stateParams.id);
        this.signalrService.on("edited", this.onSocketEditedDelegate);
        this.signalrService.on("deleted", this.onSocketDeletedDelegate);
        this.signalrService.on("relationshipEdited", this.onSocketRelationshipEdited);
    }

    public unsubscribe() {
        // console.log("Unsubscribing...");
        let entityType = this.webSocketEntityType();
        if (!entityType || !this.$stateParams.id) return;
        this.signalrService.invoke("unsubscribe", this.$rootScope.company.uniqueName, entityType, this.$stateParams.id);
        this.signalrService.off("edited", this.onSocketEditedDelegate);
        this.signalrService.off("deleted", this.onSocketDeletedDelegate);
        this.signalrService.off("relationshipEdited", this.onSocketRelationshipEdited);
    }

    private relationshipEditedCallbacks: any = {};
    public onRelationshipEdited(property: string, callback: (entity: any) => void) {
        this.relationshipEditedCallbacks[property] = callback;
    }
}
